/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { Settings, User } from 'lucide-react';
import { Pill } from './Pill';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, HStack, Popover, Portal, Text, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { isSystemManaged, resolveStewardship, type ArtifactSteward } from '@artifact-stewardship';
import type { TransferImpactDto } from '@transfer-impact';
import { ConfirmDialog } from './ConfirmDialog';
import { ControlHover } from './ControlHover';
import {
  describeTransfer,
  stewardFromValue,
  stewardValue,
  transferConfirmLabel,
  transferTitle,
} from './owner-transfer';
import { artifactService } from '../../services/artifact.service';
import { useAuth } from '../../hooks/useAuth';
import { useUsers } from '../../hooks/useUsers';
import type { ArtifactTypeName } from '../../types/artifact-usage';

/** The artifact a transfer acts on. */
export interface OwnerTransferTarget {
  type: ArtifactTypeName;
  id: string;
}

export interface OwnerControlProps {
  ownerId?: string;
  /** Who manages it (`OT5`). Read through `resolveStewardship`, never directly. */
  managedBy?: string;
  /** Shown in the hover only: the author is history, the owner is who the label names (`#900`). */
  createdBy?: string;
  /**
   * The owner's name where the surface already has it — the demand source list decorates `owner`
   * server-side. **Optional on purpose**: absent means *resolve it here*, from the users an admin can
   * list, which is the correct default rather than a capability that goes missing.
   */
  ownerName?: string;
  visibility?: 'public' | 'private';

  /**
   * **How much room the surface has — required** (Todd, 2026-09-25, rehearsing `3d`).
   *
   * `pill` is for a row: the owner in a pill beside the visibility pill, clicked to change, with the
   * hover saying so — the treatment visibility already has. `explicit` is for a detail or edit
   * surface, where there is room to name the act: the owner and a *Transfer…* button. Both open the
   * same list anchored to the control, so neither reflows its row.
   */
  presentation: 'pill' | 'explicit';

  /**
   * **What a transfer acts on — required and nullable** (`OT6`, `#1134`'s rule). `null` is a surface
   * declining, and saying so: a form in create mode has nothing stored to transfer, and a read-only
   * preview transfers nothing. An optional prop here is how transfer rendered on six surfaces with
   * no confirm and on the seventh not at all.
   */
  transfer: OwnerTransferTarget | null;

  /** Whether the viewer may change this artifact. A viewer who may not has no transfer to offer. */
  canEdit: boolean;

  /**
   * **Make it public before the transfer** (`OT4`) — required and nullable. `null` where the surface
   * cannot change reach immediately, and then the confirm does not offer it. It runs **first**: an
   * ad-ops owner promoting their own private source cannot change its reach afterwards.
   */
  makePublic: (() => Promise<unknown>) | null;
}

interface Pending {
  target: ArtifactSteward;
  label: string;
  alsoPublic: boolean;
  impact: TransferImpactDto | null;
  loadError: string | null;
}

/**
 * **Who owns this, and the one way to change it** (`OT6`, slice `3d`, `#900`).
 *
 * One control for every artifact type and every surface that shows ownership, so the label and the
 * confirm cannot differ between them. **A transfer happens when confirmed, never on a form's save**
 * — an ordinary save leaves ownership as stored (`OT1`).
 *
 * **The list offers only the users the viewer can see** (`OT6`, Todd 2026-09-25). Today that is
 * every user for an admin and none for anyone else, because `GET /api/users` is admin-only, so a
 * non-admin is never shown a list: their only act is *Promote to system*, which opens its confirm
 * directly. The list is not requested for them at all, so its 403 cannot render as a failure.
 * Groups (`#980`) change what the endpoint returns, and this control needs no change for it.
 */
export const OwnerControl: React.FC<OwnerControlProps> = ({
  ownerId,
  managedBy,
  createdBy,
  ownerName,
  visibility,
  presentation,
  transfer,
  canEdit,
  makePublic,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const offersTransfer = transfer !== null && canEdit;
  const { data: users } = useUsers(offersTransfer && isAdmin);
  const queryClient = useQueryClient();

  const stewardship = resolveStewardship({ managedBy, ownerId });
  const system = isSystemManaged({ managedBy, ownerId });
  const orphan = stewardship === 'owner' && !ownerId;
  // Shipped content (`RQ14`): managed upstream, so it names bidBrain and offers no transfer, which the
  // access stamp already withholds (`canEdit` is false for everyone).
  const shipped = stewardship === 'bidbrain';

  const nameOf = (id: string): string => {
    if (user?.id === id) return 'You';
    if (id === ownerId && ownerName) return ownerName;
    const person = (users ?? []).find((u) => u.id === id);
    return person?.name || person?.email || `${id.slice(0, 8)}…`;
  };
  const label = shipped ? 'bidBrain' : system ? 'System' : orphan ? 'No owner' : ownerId ? nameOf(ownerId) : 'System';

  const options = useMemo(() => {
    const people = (users ?? [])
      .filter((u) => u.id !== ownerId && !u.disabled)
      .map((u) => ({ value: stewardValue({ kind: 'user', userId: u.id }), label: u.name || u.email }));
    return system ? people : [{ value: 'system', label: 'System' }, ...people];
  }, [users, ownerId, system]);

  const [picking, setPicking] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch what the transfer would change, and again when *also make it public* is toggled, so the
  // confirm states the outcome of the acts the operator actually chose (`OT4`).
  useEffect(() => {
    if (!pending || !transfer || pending.impact || pending.loadError) return;
    let cancelled = false;
    artifactService
      .transferImpact(transfer.type, transfer.id, pending.target, pending.alsoPublic ? 'public' : undefined)
      .then((impact) => !cancelled && setPending((p) => (p ? { ...p, impact } : p)))
      .catch(
        () =>
          !cancelled &&
          setPending((p) => (p ? { ...p, loadError: 'bidBrain could not say what this transfer would change.' } : p)),
      );
    return () => {
      cancelled = true;
    };
  }, [pending, transfer]);

  // A non-admin can name no user, so the only act is a promotion — named as one, and absent when
  // there is nothing to promote.
  const canPickUsers = isAdmin;
  const actionable = offersTransfer && (canPickUsers || !system);
  const actionLabel = canPickUsers ? (system || orphan ? 'Assign…' : 'Transfer…') : 'Promote to system…';

  const begin = (target: ArtifactSteward, targetLabel: string) => {
    setError(null);
    setPending({ target, label: targetLabel, alsoPublic: false, impact: null, loadError: null });
  };

  const confirm = async () => {
    if (!pending || !transfer) return;
    setWorking(true);
    try {
      if (pending.alsoPublic && makePublic) await makePublic();
      await artifactService.transfer(transfer.type, transfer.id, pending.target);
      // Ownership decides what every list, picker and used-by panel shows this viewer, so every
      // query is stale — transfers are rare enough that the blunt answer is the right one.
      await queryClient.invalidateQueries();
      setPending(null);
    } catch {
      setPending(null);
      setError(
        pending.alsoPublic
          ? 'The transfer did not complete. Check its visibility and owner before trying again.'
          : 'The transfer did not complete, so the owner is unchanged.',
      );
    } finally {
      setWorking(false);
    }
  };

  /**
   * What the hover says — the owner, and what a click does. The same sentence is in the accessible
   * tree through `ControlHover` (`#1276`), so a keyboard user is told what a mouse user is.
   */
  const hover = [
    system
      ? 'Managed by the platform.'
      : orphan
        ? 'Its owner no longer exists.'
        : `Owned by ${label === 'You' ? 'you' : label}.`,
    createdBy ? `Created by ${createdBy}.` : null,
    actionable
      ? canPickUsers
        ? 'Click to transfer it to another user or to system.'
        : 'Click to promote it to system.'
      : null,
  ]
    .filter(Boolean)
    .join(' ');

  // **Neutral, with an icon, not an emoji** (`LP9`): `⚙`/`👤` rendered differently per platform and
  // read as decoration. Warning tone only where something needs attention — an owner who no longer
  // exists. The click does not reach a row: the wrapper below stops it, and the admin's list opens
  // from the popover trigger around this, so the pill itself must not stop the click (`OT6`).
  const pill = (
    <Pill
      tone={orphan ? 'warning' : 'neutral'}
      icon={system ? Settings : User}
      italic={system}
      onClick={actionable ? () => (!canPickUsers ? begin({ kind: 'system' }, 'System') : undefined) : undefined}
      propagate
    >
      {system ? 'System' : label}
    </Pill>
  );

  const explicit = (
    <HStack gap={2} display="inline-flex" alignItems="center" whiteSpace="nowrap">
      <Text
        as="span"
        fontSize="sm"
        color={orphan ? 'warning.ink' : 'fg.default'}
        fontStyle={system ? 'italic' : undefined}
      >
        {label}
      </Text>
      {actionable && (
        <Button
          size="2xs"
          variant="outline"
          borderColor="accent.solid"
          color="accent.solid"
          onClick={() => {
            if (!canPickUsers) begin({ kind: 'system' }, 'System');
          }}
        >
          {actionLabel}
        </Button>
      )}
    </HStack>
  );

  // The hover wraps the trigger itself, inside the popover, so `ControlHover` binds its sentence to the
  // element the operator reaches (`aria-describedby`), not to a popover root that renders no element.
  // On the explicit presentation the button already names the act, so only a control with nothing to
  // do gets the sentence.
  const trigger = (
    <ControlHover text={presentation === 'pill' || !actionable ? hover : null}>
      {presentation === 'pill' ? pill : explicit}
    </ControlHover>
  );

  // An admin picks from a list anchored to the control, so the row does not reflow (Todd,
  // 2026-09-25: *"it should still be a single row"*). Anyone else has one act and goes straight to
  // its confirm. A popover of plain buttons rather than a `Menu`: a menu selects only its
  // *highlighted* item, which is driven by pointer movement, and a choice that cannot be exercised by
  // clicking it cannot be tested the way an operator makes it.
  const control =
    actionable && canPickUsers ? (
      <Popover.Root open={picking} onOpenChange={({ open }) => setPicking(open)} lazyMount unmountOnExit>
        <Popover.Trigger asChild>
          <Box as="span" display="inline-flex">
            {trigger}
          </Box>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content w="auto" minW="200px" p={1}>
              <Text fontSize="xs" color="fg.muted" px={2} py={1}>
                Transfer to
              </Text>
              <VStack align="stretch" gap={0} role="list">
                {options.map((o) => (
                  <Button
                    key={o.value}
                    role="listitem"
                    size="sm"
                    variant="ghost"
                    justifyContent="flex-start"
                    fontWeight="normal"
                    onClick={() => {
                      const target = stewardFromValue(o.value);
                      setPicking(false);
                      if (target) begin(target, o.label);
                    }}
                  >
                    {o.label}
                  </Button>
                ))}
              </VStack>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    ) : (
      trigger
    );

  const statement = pending?.impact ? describeTransfer(pending.impact, pending.label, pending.alsoPublic) : null;
  const offerPublic = makePublic !== null && visibility === 'private' && pending?.target.kind === 'system';

  return (
    // **Nothing inside reaches the row.** List rows navigate on click, and React bubbles events from a
    // portal — the menu, the dialog — through the component tree, so choosing an owner would
    // otherwise also open the artifact.
    <Box as="span" display="inline-flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
      {control}

      {error && (
        <Text as="span" fontSize="xs" color="error.ink" ml={2} role="alert">
          {error}
        </Text>
      )}

      {pending && (
        <ConfirmDialog
          title={transferTitle(pending.target, pending.label)}
          tone={statement?.tone ?? 'neutral'}
          // The act is known before its consequence is, so the button names it from the start.
          confirmLabel={working ? 'Working…' : transferConfirmLabel(pending.target)}
          body={
            <Box onClick={(e) => e.stopPropagation()}>
              {pending.loadError ? (
                <Text fontSize="sm">{pending.loadError} Nothing has changed.</Text>
              ) : !statement ? (
                <Text fontSize="sm">Working out what this changes…</Text>
              ) : (
                <Box as="ul" pl={4} fontSize="sm">
                  {statement.lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </Box>
              )}
              {offerPublic && (
                <label style={{ display: 'flex', gap: 8, marginTop: 12, fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={pending.alsoPublic}
                    onChange={(e) =>
                      setPending({ ...pending, alsoPublic: e.target.checked, impact: null, loadError: null })
                    }
                  />
                  Also make it public, so it stays visible to everyone who can see it now or more
                </label>
              )}
            </Box>
          }
          onConfirm={() => {
            // Nothing is confirmed until the consequence has been stated.
            if (statement && !working) void confirm();
          }}
          onCancel={() => !working && setPending(null)}
        />
      )}
    </Box>
  );
};
